import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '@/components/utils/translations';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { liveDealTypeDisplay } from '@/lib/procurementMethod';
import { Calendar, FileText, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import {
  normalizeDeal,
  getDeadlineHorizon,
  getDeadlineHorizonColor,
  resolveAttachmentUrl,
} from '@/utils/procurementDealUtils';
import { toOfficialCategoryName, getProfileProcurementCategories } from '@/utils/edtechCategoryNames';
import { getDealCategories, getDealCategoryDisplay } from '@/utils/procurementCategoryUtils';
import { logTenderReviewed, extractAccountId, getCurrentTenantId } from '@/components/utils/analytics';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import SourceLink from '@/components/procurement/SourceLink';
import ProductFitPanel from '@/components/procurement/ProductFitPanel';
import { fetchFitLatest } from '@/lib/api/fit';
import { resolveDealId } from '@/lib/api/myTenders';
import DealStatusPicker from '@/components/procurement/DealStatusPicker';
import DealValueDisplay from '@/components/procurement/DealValueDisplay';

const formatSafeDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? '—' : format(d, 'dd MMM yyyy');
  } catch {
    return '—';
  }
};

function toStringArray(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v)).filter(Boolean);
      }
    } catch {
      // ignore malformed JSON
    }

    return trimmed
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  return [];
}

function dealMatchesProfileCategories(deal, profile) {
  if (!profile) return true;

  const dealCats = getDealCategories(deal);
  if (dealCats.length === 0) return true;

  const profileCats = getProfileProcurementCategories(profile).map(
    (c) => toOfficialCategoryName(c) || String(c).trim(),
  );

  if (profileCats.length === 0) return true;

  return dealCats.some((dealCat) => {
    const dealLower = String(dealCat || '').toLowerCase();
    return profileCats.some((profileCat) => {
      const profileLower = String(profileCat || '').toLowerCase();
      return (
        profileLower === dealLower ||
        dealLower.includes(profileLower) ||
        profileLower.includes(dealLower)
      );
    });
  });
}

function dealMatchesAnyActiveProfile(deal, profiles) {
  if (!profiles?.length) return dealMatchesProfileCategories(deal, null);
  return profiles.some((p) => dealMatchesProfileCategories(deal, p));
}

// Fit labels arrive as English enum values (also used for colours / sorting); translate only for display.
const FIT_LABEL_KEYS = {
  'Strong Fit': 'strongFit',
  'Moderate Fit': 'moderateFit',
  'Weak Fit': 'procWeakFit',
  'No Fit': 'procNoFit',
  'Insufficient Info': 'insufficientInfo',
  'Low Evidence': 'procLowEvidence',
};

function FitBadge({ deal, activeProfiles, fitOverride }) {
  const { t } = useTranslation();

  const categoryMatch = dealMatchesAnyActiveProfile(deal, activeProfiles);
  if (!categoryMatch) {
    return (
      <span
        className="text-xs text-gray-500"
        title={t('procFitOutOfScopeTitle')}
      >
        {t('outOfScope')}
      </span>
    );
  }

  const loaded = fitOverride?.loaded ?? false;
  const jobStatus = fitOverride?.fit_job_status ?? deal?.fit_job_status ?? null;
  const outcome = fitOverride?.fit_light_outcome ?? deal?.fit_light_outcome;

  let label = fitOverride?.fit_light_label ?? deal?.fit_light_label;

  if (outcome === 'insufficient_information') {
    label = t('insufficientInfo');
  }

  if (label) {
    const colorMap = {
      'Strong Fit': 'bg-green-100 text-green-800 border-green-200',
      'Moderate Fit': 'bg-amber-100 text-amber-800 border-amber-200',
      'Weak Fit': 'bg-orange-100 text-orange-800 border-orange-200',
      'No Fit': 'bg-red-100 text-red-800 border-red-200',
      'Insufficient Info': 'bg-gray-100 text-gray-600 border-gray-200',
      'Low Evidence': 'bg-gray-100 text-gray-600 border-gray-200',
    };

    const colors = colorMap[label] || 'bg-gray-100 text-gray-600 border-gray-200';

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors}`}>
        {FIT_LABEL_KEYS[label] ? t(FIT_LABEL_KEYS[label]) : label}
      </span>
    );
  }

  if (jobStatus === 'queued' || jobStatus === 'processing') {
    return <span className="text-xs text-gray-400">{t('pending')}</span>;
  }

  if (!loaded) {
    return <span className="text-xs text-gray-400">{t('procLoading')}</span>;
  }

  return <span className="text-xs text-gray-400">—</span>;
}

const SortableHeader = ({ children, sortKey, sortConfig, setSortConfig, className, title: titleAttr }) => {
  const isSorted = sortConfig.key === sortKey;
  const direction = isSorted ? sortConfig.direction : 'none';
  const handleSort = () => {
    setSortConfig({
      key: sortKey,
      direction: direction === 'ascending' ? 'descending' : 'ascending',
    });
  };
  return (
    <TableHead title={titleAttr} onClick={handleSort} className={`cursor-pointer hover:bg-gray-50 ${className || ''}`}>
      <div className="flex items-center gap-2">
        {children}
        {isSorted && (direction === 'ascending' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </div>
    </TableHead>
  );
};

const FIT_POLL_MS = 15000;
const FIT_CONCURRENCY = 8;
/** Cap DOM rows — Recently Expired can be 5k+ deals. */
const PAGE_SIZE = 50;

function LiveRadarList({
  deals,
  sortConfig,
  setSortConfig,
  onRowClick,
  onDealViewLog,
  currentTenant,
  activeProductProfiles = [],
  suspendFitLoading = false,
  onFitOverridesChange,
  dealStatusByDealId,
  onStatusChange,
}) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [fitOverrides, setFitOverrides] = useState(() => new Map());
  const [page, setPage] = useState(1);
  const fitOverridesRef = useRef(new Map());

  const currentUser = contextUser;
  const effectiveTenant = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant || contextTenant,
    contextUser,
    'instructure'
  );

  const normalizedDeals = React.useMemo(
    () => (deals || []).map((d) => normalizeDeal(d, d.account)).filter((d) => d && d.id),
    [deals]
  );

  const dealsSignature = `${normalizedDeals.length}:${normalizedDeals[0]?.id ?? ''}:${normalizedDeals[normalizedDeals.length - 1]?.id ?? ''}`;
  useEffect(() => {
    setPage(1);
  }, [dealsSignature]);

  const totalPages = Math.max(1, Math.ceil(normalizedDeals.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);
  const pageDeals = React.useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return normalizedDeals.slice(start, start + PAGE_SIZE);
  }, [normalizedDeals, safePage]);

  const fetchFitForDeals = useCallback(async () => {
    if (suspendFitLoading || !effectiveTenant || !activeProductProfiles?.length) return;

    const toFetch = pageDeals.filter((d) => {
      if (!d.fit_deal_id) return false;
      if (!dealMatchesAnyActiveProfile(d, activeProductProfiles)) return false;

      const key = `${effectiveTenant}:${d.fit_deal_id}`;
      const override = fitOverridesRef.current.get(key);

      const rowHasSavedFit =
        !!(d.fit_light_label || override?.fit_light_label);

      const activeJob =
        (override?.fit_job_status === 'queued' || override?.fit_job_status === 'processing') ||
        (d.fit_job_status === 'queued' || d.fit_job_status === 'processing');

      if (activeJob) return true;
      if (rowHasSavedFit) return false;
      if (override?.loaded) return false;

      return true;
    });

    if (toFetch.length === 0) return;

    const queue = [...toFetch];
    const results = [];

    async function runWorker() {
      while (queue.length > 0) {
        const deal = queue.shift();
        if (!deal) break;

        const response = await fetchFitLatest(effectiveTenant, deal.fit_deal_id).catch(() => null);

        if (response) {
          results.push({
            key: `${effectiveTenant}:${deal.fit_deal_id}`,
            data: response,
          });
        }
      }
    }

    await Promise.all(Array.from({ length: FIT_CONCURRENCY }, runWorker));

    const next = new Map(fitOverridesRef.current);

    for (const result of results) {
      const light = result.data?.light || null;
      const lightJob = result.data?.light_job || null;

      next.set(result.key, {
        loaded: true,
        fit_light_label: light?.label ?? null,
        fit_light_score: light?.score ?? null,
        fit_light_outcome: light?.outcome ?? null,
        fit_job_status: lightJob?.status ?? null,
      });
    }

    fitOverridesRef.current = next;
    setFitOverrides(new Map(next));
  }, [suspendFitLoading, effectiveTenant, activeProductProfiles, pageDeals]);

  /** Keep table Fit column in sync after drawer refresh (POST + GET latest). */
  const mergeFitLatestIntoOverrides = useCallback(
    (fitDealId, latest) => {
      if (!fitDealId || !effectiveTenant || !latest) return;
      const key = `${effectiveTenant}:${fitDealId}`;
      const light = latest?.light ?? null;
      const lightJob = latest?.light_job ?? null;

      const next = new Map(fitOverridesRef.current);
      next.set(key, {
        loaded: true,
        fit_light_label: light?.label ?? null,
        fit_light_score: light?.score ?? null,
        fit_light_outcome: light?.outcome ?? null,
        fit_job_status: lightJob?.status ?? null,
      });
      fitOverridesRef.current = next;
      setFitOverrides(new Map(next));
      console.log('[LiveRadar] page-level fit map updated for dealId', { fitDealId, key });
    },
    [effectiveTenant]
  );

  const activeProfileIdsKey = (activeProductProfiles || []).map((p) => p.id).sort().join(',');

  useEffect(() => {
    fitOverridesRef.current = new Map();
    setFitOverrides(new Map());
  }, [effectiveTenant, activeProfileIdsKey]);

  useEffect(() => {
    fetchFitForDeals();
    const interval = setInterval(fetchFitForDeals, FIT_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchFitForDeals]);

  useEffect(() => {
    onFitOverridesChange?.(fitOverrides);
  }, [fitOverrides, onFitOverridesChange]);

  const showStatusColumn = dealStatusByDealId != null && onStatusChange != null;

  const handleRowClick = (deal) => {
    if (currentUser) {
      logTenderReviewed({
        tenantId: getCurrentTenantId(currentUser, 'instructure'),
        userId: currentUser.id,
        accountId: deal.account_id || extractAccountId(deal.account),
        tenderId: deal.id,
        source: 'procurement_page',
        metadata: { tender_title: deal.title, category: deal.category, account_name: deal.institution },
      }).catch(() => {});
    }
    setSelectedDeal(deal);
    setDrawerOpen(true);
    onRowClick?.(deal);
  };

  // Record My Deals view whenever the detail sheet is actually open with a deal.
  // Idempotent on the BE (upsert per user/deal/day) — safe to re-fire on reopen.
  const openDealId = drawerOpen && selectedDeal ? resolveDealId(selectedDeal) : null;
  useEffect(() => {
    if (!openDealId) return;
    onDealViewLog?.(openDealId);
  }, [openDealId, onDealViewLog]);

  return (
    <>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              {showStatusColumn && (
                <TableHead className="w-10 shrink-0" aria-label={t('procTeamStatusAria')} />
              )}
              <SortableHeader sortKey="title" sortConfig={sortConfig} setSortConfig={setSortConfig} className="min-w-[180px]">
                {t('title')}
              </SortableHeader>
              <SortableHeader sortKey="institution" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                {t('institution')}
              </SortableHeader>
              <SortableHeader sortKey="country" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                {t('country')}
              </SortableHeader>
              <SortableHeader sortKey="category" sortConfig={sortConfig} setSortConfig={setSortConfig} className="w-28">
                {t('category')}
              </SortableHeader>
              <SortableHeader
                sortKey="deal_type"
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                className="min-w-[120px] w-36"
                title="Procurement pathway (§4). A framework vehicle, where the notice names one, is shown underneath."
              >
                {t('type')}
              </SortableHeader>
              <SortableHeader sortKey="deadline" sortConfig={sortConfig} setSortConfig={setSortConfig} className="min-w-[110px]">
                {t('deadline')}
              </SortableHeader>
              <SortableHeader sortKey="estimated_value" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                {t('value')}
              </SortableHeader>
              <SortableHeader
                sortKey="fit"
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                className="min-w-[120px] w-32"
                title={t('procFitColumnTitle')}
              >
                {t('fit')}
              </SortableHeader>
              <TableHead className="w-14 shrink-0" aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageDeals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showStatusColumn ? 10 : 9} className="text-center py-12 text-gray-500">
                  {t('noLiveDeals')}
                </TableCell>
              </TableRow>
            ) : (
              pageDeals.map((deal) => {
                const horizon = getDeadlineHorizon(deal.deadline);
                const horizonColor = getDeadlineHorizonColor(horizon.label);
                const dealId = resolveDealId(deal);
                const statusAugment = dealId ? dealStatusByDealId?.[dealId] : null;
                return (
                  <TableRow
                    key={deal.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(deal)}
                  >
                    {showStatusColumn && dealId && (
                      <TableCell
                        className="w-10 shrink-0 text-center relative z-10"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <DealStatusPicker
                          dealId={dealId}
                          augment={statusAugment || {}}
                          variant="dot"
                          onStatusChange={onStatusChange}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium min-w-[180px] max-w-[300px] truncate">
                      {deal.title || '—'}
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate">{deal.institution || '—'}</TableCell>
                    <TableCell>{deal.country || '—'}</TableCell>
                    <TableCell className="w-32 shrink-0">
                      <Badge variant="secondary" className="text-xs">
                        {getDealCategoryDisplay(deal) || deal.category || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs align-top whitespace-normal break-words w-36">
                      {(() => {
                        const m = liveDealTypeDisplay(deal);
                        if (!m.method && !m.framework) return '—';
                        return (
                          <>
                            {m.method ? <p>{m.method}</p> : null}
                            {m.framework ? (
                              <p className="text-gray-500 mt-0.5" title={m.frameworkTitle || undefined}>{m.framework}</p>
                            ) : null}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge variant="outline" className={`text-xs border ${horizonColor}`}>
                        <Calendar className="w-3 h-3 mr-1 inline" />
                        {deal.deadline ? formatSafeDate(deal.deadline) : horizon.label}
                      </Badge>
                    </TableCell>
                    <TableCell><DealValueDisplay deal={deal} /></TableCell>
                    <TableCell className="whitespace-nowrap">
                      <FitBadge deal={deal} activeProfiles={activeProductProfiles} fitOverride={fitOverrides.get(`${effectiveTenant}:${deal.fit_deal_id}`)} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {(() => {
                          const candidates = [
                            ...(Array.isArray(deal.pdf_attachment_urls) ? deal.pdf_attachment_urls : deal.pdf_attachment_urls ? [deal.pdf_attachment_urls] : []),
                            ...(Array.isArray(deal.attachment_urls) ? deal.attachment_urls : deal.attachment_urls ? [deal.attachment_urls] : []),
                          ];
                          const firstUrl = candidates.map((u) => resolveAttachmentUrl(u)).find(Boolean);
                          if (deal.has_pdf_attachment || firstUrl) {
                            const icon = <FileText className="w-4 h-4 text-gray-500" title={firstUrl ? 'Open attachment' : 'Has PDF'} />;
                            return firstUrl ? (
                              <a href={firstUrl} target="_blank" rel="noopener noreferrer" className="inline-flex text-gray-600 hover:text-[#00C2C2]" onClick={(e) => e.stopPropagation()}>
                                {icon}
                              </a>
                            ) : (
                              icon
                            );
                          }
                          return null;
                        })()}
                        {deal.source_url && (
                          <SourceLink
                            url={deal.source_url}
                            variant="icon"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {normalizedDeals.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 px-1">
          <p className="text-xs text-muted-foreground">
            {(() => {
              const start = (safePage - 1) * PAGE_SIZE + 1;
              const end = Math.min(safePage * PAGE_SIZE, normalizedDeals.length);
              return `${start}–${end} of ${normalizedDeals.length.toLocaleString()}`;
            })()}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setPage((p) => Math.max(1, Math.min(p, totalPages) - 1))}
                disabled={safePage <= 1}
                aria-label={t('previous')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground px-2 tabular-nums">
                {safePage} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setPage((p) => Math.min(totalPages, Math.min(p, totalPages) + 1))}
                disabled={safePage >= totalPages}
                aria-label={t('next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedDeal && (
            <>
              <SheetHeader>
                <SheetTitle className="text-lg">{selectedDeal.title || '—'}</SheetTitle>
              </SheetHeader>
              <div className="mt-8 space-y-6">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{t('institution')}</p>
                  <p className="text-base font-medium text-[#0A1A2F]">{selectedDeal.institution || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{t('country')}</p>
                  <p className="text-base font-medium text-[#0A1A2F]">{selectedDeal.country || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{t('category')}</p>
                  <Badge variant="secondary" className="text-sm">{getDealCategoryDisplay(selectedDeal) || selectedDeal.category || '—'}</Badge>
                </div>
                {(() => {
                  const m = liveDealTypeDisplay(selectedDeal);
                  if (!m.method && !m.framework) return null;
                  return (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{t('type')}</p>
                      {m.method ? <p className="text-base font-medium text-[#0A1A2F]">{m.method}</p> : null}
                      {m.framework ? (
                        <p className="text-sm text-gray-500 mt-0.5" title={m.frameworkTitle || undefined}>{m.framework}</p>
                      ) : null}
                    </div>
                  );
                })()}
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{t('deadline')}</p>
                  <p className="text-base font-medium text-[#0A1A2F]">{selectedDeal.deadline ? formatSafeDate(selectedDeal.deadline) : '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{t('value')}</p>
                  <p className="text-base font-medium text-[#0A1A2F]"><DealValueDisplay deal={selectedDeal} showOriginalCurrency /></p>
                </div>
                {selectedDeal.incumbent_vendor && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Incumbent vendor</p>
                    <p className="text-base font-medium text-[#0A1A2F]">{selectedDeal.incumbent_vendor}</p>
                  </div>
                )}
                {selectedDeal.source_url && (
                  <div className="pt-2">
                    <SourceLink url={selectedDeal.source_url} variant="text" />
                  </div>
                )}
                <ProductFitPanel
                  deal={selectedDeal}
                  tenantId={effectiveTenant}
                  onFitLatestSynced={mergeFitLatestIntoOverrides}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

export default React.memo(LiveRadarList);
